import { Accordion, Clickable, H2, H3, P, Section } from '@ahmdigital/ui';
import { get } from 'lodash/fp';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import AccordionActions from '../../../../../actions/accordion';

const StyledSection = styled(Section)`
  padding: 0;
  margin-bottom: 2rem;
`;

const StyledAccordion = styled(Accordion)`
  margin-bottom: 2rem;
`;

const defaultFAQ = {
  id: 'life-help-and-support-faq',
  items: [
    {
      body: (
        <P>
          ahm life insurance products are issued by Hannover Life Re of Australasia Ltd ABN 37 062 395 484, AFSL 530811
          Tower 1, Level 33, 100 Barangaroo Avenue, Sydney NSW 2000, Australia (Hannover). Medibank Private Limited ABN
          74 000 218 306, AR 286089 (Medibank Private), promoted the insurance as the authorised representative of
          Greenstone Financial Services PTY Ltd, ABN 53 128 692 884, Australian Financial Services Licence No. 343079
          (GFS), 58 Norwest Boulevard, Norwest NSW 2153. These ahm life insurance products are administered by GFS.
        </P>
      ),
      title: 'Who is the insurer?',
    },
    {
      body: (
        <P>
          Premiums are a regular payment to cover the cost of your insurance policy. The cost of your cover is
          calculated based on personal risk factors such age, gender, and lifestyle habits such as smoking. The amount
          of cover you have and any optional extras influence the amount you need to pay.
        </P>
      ),
      title: 'How is the cost of my cover calculated?',
    },
    {
      body: (
        <>
          <P>
            Yes. Your life insurance premium is calculated on a stepped basis, where premiums are recalculated each year
            based on your increase in age. This means your premium will increase each year as you get older to reflect
            your increased insurance risk.
          </P>
          <P>
            In addition, ahm products include an automatic increase benefit to ensure your policy keeps up with the
            rising cost of living. Your life benefit amount will be increased by 5% each year on your policy
            anniversary. This means your premium will also increase to reflect the higher level of cover. You can opt
            out of these automatic increases by contacting us. Please refer to your Product Disclosure Statement for
            full details.
          </P>
          <P>
            ahm may, from time to time, review its premium base rates. Base rates are the starting point for calculating
            premiums. Each customers personal risk circumstances such as gender, health and lifestyles factors are added
            on top of the base premium. Base premiums can be influenced by factors impacting the individual insurer or
            the industry as a whole such as changing mortality rates, inflation or interest rates, which can affect the
            cost of providing insurance. Insurers may increase base rates to maintain their ability to cover future
            claims. Insurers may also decrease their insurance premiums in some circumstances where it’s possible to do
            so.
          </P>
          <P>
            Premium changes happen on the next anniversary date of your insurance policy. We’ll get in touch with you
            before your policy anniversary to let you know your new premium for the following year.
          </P>
          <P>For more information, please refer to your Product Disclosure Statement.</P>
        </>
      ),
      title: 'Will my life insurance premiums increase?',
    },
    {
      body: (
        <>
          <P>
            Yes. Once your insurance policy starts, you may be eligible to increase the amount of your cover. Any
            application you make to increase your cover will be subject to eligibility criteria, including age
            requirements, and underwriting assessment in the normal manner.
          </P>
          <P>
            In some circumstances, depending on your insurance policy, you may be entitled to an increase without the
            need for underwriting assessment. Your Product Disclosure Statement sets out the full details on how to
            increase your cover.
          </P>
        </>
      ),
      title: 'Can I increase my life cover?',
    },
    {
      body: (
        <>
          <P>
            You can cancel your insurance policy within 30 days of the first premium due date and provided you haven’t
            made a claim, we’ll cancel the insurance policy and refund any premiums you may have paid. This is known as
            the ‘cooling-off period’.{' '}
          </P>
          <P>
            If you cancel your insurance policy after the 30-day cooling-off period, you won’t get a refund of the
            premiums you’ve paid. If you paid your premium annually, a refund of premiums for any complete months
            remaining on your Insurance Policy will be made.
          </P>
        </>
      ),
      title: 'Will I get a refund if I cancel my life insurance policy?',
    },
    {
      body: (
        <P>
          ahm cannot offer Life Insurance cover to individuals with a diagnosis of HIV or AIDS at the time of
          application, reinstatement, cover increase, or when applying for optional benefits or additional lives
          insured. That’s because we don’t use medical examinations as part of the application process, and so we are
          unable to assess the severity of the condition. However, you will not be excluded if you contract HIV or AIDS
          after you have purchased a policy. That means you may retain your policy, and we shall pay claims related to a
          HIV or AIDS illness, provided the claim meets the appropriate definitions of the policy.
        </P>
      ),
      title: 'Can I apply for ahm life insurance if I have HIV/AIDS?',
    },
  ],
};

const FAQ = () => {
  const openDefaultIndices = useSelector(get(`accordion.openIndices.${defaultFAQ.id}`));
  const dispatch = useDispatch();

  return (
    <StyledSection id="faqs">
      <H2 variant="marginBottomSpacer10">Frequently asked questions</H2>
      <P>
        If you still have questions about your current life insurance policy purchased on or before 30 June 2023, we’ve
        got answers to common questions right here.
      </P>
      <StyledAccordion
        variant="white"
        items={defaultFAQ.items}
        id={defaultFAQ.id}
        openIndices={openDefaultIndices}
        updateSelection={(newOpenIndices) =>
          AccordionActions.updateAccordionWithId({ id: defaultFAQ.id, openIndices: newOpenIndices })(dispatch)
        }
      />
      <>
        <H3 looksLike="h4">Important information about ahm Life Insurance and COVID-19</H3>
        <P>
          From juggling working from home with kids or staying at home more and enjoying a slower pace, everyone’s life
          has changed in some way due to the COVID-19 pandemic.
          <br />
          <br />
          Regardless of your situation, it’s good to understand your life insurance cover. We want to reassure you that
          none of our ahm life insurance policies has a specific exclusion for a pandemic such as COVID-19.
          <br />
          <br />
          This means if you are an existing customer, you are covered regardless of whether or not you have COVID-19.
          <br />
          <br />
          For more information, please download our PDS found on this page or by contact us on{' '}
          <Clickable to="tel:1300052589">1300 052 589</Clickable>.
        </P>
      </>
    </StyledSection>
  );
};

export default FAQ;
